import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'
import {
  StyledTableCell,
  StyledTableRow,
} from './invoice-documents-table.styles'
import { ListDocumentsResponse } from 'store/invoices/invoices.types'

import Delete from 'assets/icons/Delete.png'
import InvoiceDocumentsTableDocType from './invoice-documents-table-document-type'
import InvoiceDocument from '../../invoice-document/invoice-document.component'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  useDeleteInvoiceDocumentMutation,
  useListInvoicedocumentsQuery,
} from 'store/invoices/invoices.api'
import TranspotsLoading from 'components/transpots-loading/transpots-loading.component'
import ConfirmationDialog from 'components/confirmation-dialog/confirmation-dialog.component'
import { setInvoiceEmailAttachments } from 'store/invoices/invoices.slice'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'store'
import { isCompanyFeatureEnabled } from 'utils/features'

import { InvoiceType, isToBeInvoicedWorkspaceTab } from 'store/invoices/invoices.types'

interface IProps {
  documents: ListDocumentsResponse
  orderId: string
  invoiceId: string
  companyId?: string
  condensed?: boolean
  invoiceType?: InvoiceType
  variant?: 'receivables' | 'carrier'
  handleClose?: () => void
  refetchDocuments?: () => void
  refetchPaymentDocuments?: () => void
}

const CARRIER_DOC_ORDER = [
  'LC',
  'INVOICE',
  'BOL',
  'POD',
  'OTHER',
  'REMITTANCE',
  'PAYMENT_RECEIPT',
] as const

const InvoiceDocumentsTable = ({
  documents,
  orderId,
  invoiceId,
  companyId,
  condensed = false,
  invoiceType,
  variant = 'receivables',
  handleClose,
  refetchDocuments,
  refetchPaymentDocuments,
}: IProps) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const featuresByCompany = useSelector(
    (state: RootState) => state.auth.featuresByCompany
  )
  const showWaybill =
    !companyId ||
    isCompanyFeatureEnabled(featuresByCompany, companyId, 'WAYBILL')
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteS3Uri, setDeleteS3Uri] = useState('')
  const isCarrier = variant === 'carrier'

  const { refetch } = useListInvoicedocumentsQuery({ orderId })

  const [
    deleteDocument,
    { isLoading: isDeleteLoading, isError: isDeleteError },
  ] = useDeleteInvoiceDocumentMutation()

  const keys = useMemo(() => {
    if (isCarrier) {
      return CARRIER_DOC_ORDER.filter((key) => key in documents || true)
    }
    const docKeys = Object.keys(documents)
    const withoutWaybill = docKeys.filter(
      (key) => showWaybill || key !== 'WAYBILL'
    )
    if (invoiceType && isToBeInvoicedWorkspaceTab(invoiceType)) {
      return withoutWaybill.filter(
        (key) =>
          key !== 'REMITTANCE' &&
          key !== 'PAYMENT_RECEIPT' &&
          key !== 'OTHER'
      )
    }
    return withoutWaybill
  }, [documents, invoiceType, showWaybill, isCarrier])

  const canDeleteDocument = (key: string) => {
    if (key === 'LC' || key === 'WAYBILL') return false
    if (key === 'INVOICE') return isCarrier
    return true
  }

  const handleDelete = async () => {
    setDeleteOpen(false)
    try {
      await deleteDocument({ s3Uri: deleteS3Uri }).unwrap()

      if (!isDeleteError) {
        refetch()
      }
    } catch (error) {
      console.log(error)
    }
  }

  const fetchAllPresignedUrl = useCallback(() => {
    return Object.entries(documents)
      .filter(([key]) => showWaybill || key !== 'WAYBILL')
      .flatMap(([, arr]) =>
      arr.map((doc) => doc.s3Path)
    )
  }, [documents, showWaybill])

  useEffect(() => {
    const urls = fetchAllPresignedUrl()
    dispatch(setInvoiceEmailAttachments(urls))
  }, [dispatch, fetchAllPresignedUrl])

  return (
    <>
      <TableContainer component={Paper} sx={{ borderRadius: 0 }}>
        <Table sx={{ minWidth: 700 }} size={condensed ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              <StyledTableCell theme={theme}>Documents Type</StyledTableCell>
              <StyledTableCell theme={theme}>
                <Stack
                  direction={'row'}
                  alignItems={'center'}
                  justifyContent={'space-between'}
                >
                  <Typography>Documents</Typography>
                  <Typography>Actions</Typography>
                </Stack>
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {keys.map((key) => (
              <StyledTableRow key={key} theme={theme}>
                <StyledTableCell theme={theme}>
                  <InvoiceDocumentsTableDocType
                    documentType={key}
                    orderId={orderId}
                    invoiceId={invoiceId}
                    invoiceType={invoiceType}
                    condensed={condensed}
                    handleClose={handleClose}
                    refetchDocuments={refetchDocuments}
                    refetchPaymentDocuments={refetchPaymentDocuments}
                    variant={variant}
                  />
                </StyledTableCell>
                <StyledTableCell component="th" scope="row" theme={theme}>
                  {documents?.[key]?.length === 0 && (
                    <Typography>No Documents Uploaded</Typography>
                  )}
                  {documents?.[key]?.map((document) => (
                    <Stack
                      key={document.documentName}
                      my={2}
                      direction={'row'}
                      alignItems={'center'}
                      justifyContent={'space-between'}
                    >
                      <InvoiceDocument document={document} />
                      {canDeleteDocument(key) && (
                        <img
                          src={Delete}
                          alt="Delete Document"
                          width={condensed ? 20 : 25}
                          height={condensed ? 25 : 30}
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            setDeleteOpen(true)
                            setDeleteS3Uri(document.s3Path)
                          }}
                        />
                      )}
                    </Stack>
                  ))}
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ConfirmationDialog
        title="Delete Document"
        open={deleteOpen}
        handleClose={() => setDeleteOpen(false)}
        successBtnText="Delete"
        successBtnTextStyle={{ color: 'error' }}
        handleSuccessBtn={handleDelete}
      >
        Are you sure to delete the document?
      </ConfirmationDialog>
      {isDeleteLoading && <TranspotsLoading loadingText="Deleting..." />}
    </>
  )
}

export default InvoiceDocumentsTable
